const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect } from 'react';
import {
  X,
  Minus,
  Plus,
  Heart,
  CreditCard,
  Truck,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Clock,
  Calendar,
  Info,
} from 'lucide-react';

// More reliable placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/600x800/f5f5f5/999999?text=No+Image';

// Helper function to repair base64 images
const repairBase64Image = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return PLACEHOLDER_IMAGE;

  const trimmed = imageUrl.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;

  // Fix malformed base64 (missing semicolon or comma)
  if (trimmed.startsWith('data:image/')) {
    let fixed = trimmed;

    // Fix missing semicolon before base64
    if (fixed.includes(':base64') && !fixed.includes(';base64')) {
      fixed = fixed.replace(':base64', ';base64,');
    }

    // Fix missing comma after base64
    if (fixed.includes(';base64') && !fixed.includes(';base64,')) {
      fixed = fixed.replace(';base64', ';base64,');
    }

    return fixed;
  }

  // Keep blob URLs
  if (trimmed.startsWith('blob:')) {
    return trimmed;
  }

  // Keep absolute URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  return PLACEHOLDER_IMAGE;
};

const ProductDetailModal = ({
  product,
  onClose,
  onAddToCart,
  onToggleWishlist,
  isInWishlist,
  onNavigate,
}) => {
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [mainImageError, setMainImageError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAddToCartProcessing, setIsAddToCartProcessing] = useState(false);
  const [isCheckoutProcessing, setIsCheckoutProcessing] = useState(false);
  const [stockError, setStockError] = useState(null);

  // Check for promo
  const extendedProduct = product;
  const isPromo =
    extendedProduct.is_promo === true ||
    extendedProduct.is_promo === 1 ||
    extendedProduct.is_promo === '1' ||
    (extendedProduct.promo_price && extendedProduct.promo_price > 0);

  const promoPrice = extendedProduct.promo_price;
  const promoPercentage = extendedProduct.promo_percentage;
  const promoLabel = extendedProduct.promo_label || 'SALE';
  const promoEndDate = extendedProduct.promo_end_date;

  // Check if promo is still active
  const isPromoActive = !promoEndDate || new Date(promoEndDate) >= new Date();
  const showPromo = isPromo && isPromoActive && promoPrice;

  // Calculate display prices
  const displayPrice = showPromo ? promoPrice : product.price || 0;
  const originalPrice = showPromo ? product.price : null;
  const savings = showPromo && product.price && promoPrice ? product.price - promoPrice : 0;
  const savingsPercentage =
    promoPercentage ||
    (showPromo && product.price && promoPrice
      ? Math.round(((product.price - promoPrice) / product.price) * 100)
      : 0);

  // Check if product is pre-order
  const isPreOrder = extendedProduct.isPreOrder === true;
  const preOrderReleaseDate = extendedProduct.preOrderReleaseDate;

  // Check stock
  const stock = product.stock || 0;
  const isOutOfStock = stock <= 0;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return null;
    }
  };

  // Format release date
  const formatReleaseDate = (dateString) => {
    if (!dateString) return 'Coming Soon';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return 'Coming Soon';
    }
  };

  // Create array of all available images
  const allImages = [];

  // Add main image if it exists and is valid
  if (product.image && typeof product.image === 'string' && product.image.length > 5) {
    allImages.push(repairBase64Image(product.image));
  }

  // Add gallery images if they exist
  if (product.galleryImages && Array.isArray(product.galleryImages)) {
    const validGalleryImages = product.galleryImages.filter(
      (img) => img && typeof img === 'string' && img.length > 5
    );
    allImages.push(...validGalleryImages.map((img) => repairBase64Image(img)));
  }

  // Remove duplicates
  const uniqueImages = [...new Set(allImages)];

  useEffect(() => {
    setSelectedImage(0);
    setDetailQuantity(1);
    setStockError(null);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
      setIsAddToCartProcessing(false);
      setIsCheckoutProcessing(false);
      setStockError(null);
    };
  }, [product]);

  // Get current display image with fallback
  const getCurrentImage = () => {
    if (mainImageError || !uniqueImages.length || imageErrors[selectedImage]) {
      return PLACEHOLDER_IMAGE;
    }
    return uniqueImages[selectedImage];
  };

  const handleImageError = (index) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  const handleMainImageError = () => {
    setMainImageError(true);
  };

  // Handle Add to Cart only with stock check
  const handleAddToCart = async () => {
    if (isAddToCartProcessing || isCheckoutProcessing) return;

    if (isOutOfStock) {
      setStockError('Sorry, this item is out of stock');
      return;
    }

    if (stock < detailQuantity) {
      setStockError(`Sorry, only ${stock} item(s) available in stock`);
      return;
    }

    setIsAddToCartProcessing(true);
    setStockError(null);

    try {
      const result = await Promise.resolve(onAddToCart(product, detailQuantity));

      if (result === false) {
        setStockError('Failed to add item to cart. Please try again.');
      } else {
        const toast = document.createElement('div');
        toast.className =
          'fixed top-24 right-4 bg-green-600 text-white px-6 py-3 rounded-sm shadow-lg z-[300] animate-slide-in';
        toast.textContent = 'Added to cart successfully';
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
      }
    } catch (error) {
      setStockError('Failed to add item to cart. Please try again.');
    } finally {
      setIsAddToCartProcessing(false);
    }
  };

  // Handle Checkout with session storage for page reload recovery
  const handleCheckout = () => {
    if (isCheckoutProcessing || isAddToCartProcessing) {
      return;
    }

    if (isOutOfStock) {
      setStockError('Sorry, this item is out of stock and cannot be purchased');
      return;
    }

    if (stock < detailQuantity) {
      setStockError(`Sorry, only ${stock} item(s) available in stock. Please adjust quantity.`);
      return;
    }

    const checkoutData = {
      product: {
        id: product.id,
        name: product.name,
        price: product.price,
        image: repairBase64Image(product.image),
        category: product.category,
        description: product.description,
        condition: product.condition,
        weight: product.weight,
        karat: product.karat,
        goldType: product.goldType,
        stock: product.stock,
        isPreOrder: extendedProduct.isPreOrder,
        preOrderReleaseDate: extendedProduct.preOrderReleaseDate,
        is_promo: extendedProduct.is_promo,
        promo_price: extendedProduct.promo_price,
        promo_percentage: extendedProduct.promo_percentage,
        promo_label: extendedProduct.promo_label,
      },
      quantity: detailQuantity,
      timestamp: Date.now(),
    };

    sessionStorage.setItem('directCheckout', JSON.stringify(checkoutData));
    onClose();
    onNavigate('checkout');
  };

  const nextImage = () => {
    setSelectedImage((prev) => Math.min(uniqueImages.length - 1, prev + 1));
  };

  const prevImage = () => {
    setSelectedImage((prev) => Math.max(0, prev - 1));
  };

  // Get condition display information
  const getConditionInfo = () => {
    const condition = product.condition || 'Brand New';

    switch (condition) {
      case 'Pre-loved':
      case 'Pre-loved (Used)':
        return {
          label: 'Pre-loved',
          badgeColor: 'bg-amber-100 text-amber-800 border-amber-200',
          description: 'This item is pre-owned and has been carefully inspected.',
          detailedInfo: 'Authenticity guaranteed, carefully inspected, like brand new',
        };
      case 'Vintage':
        return {
          label: 'Vintage',
          badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
          description: 'A vintage piece with character and history.',
          detailedInfo: 'Authentic vintage, character with age, one-of-a-kind',
        };
      case 'Like New':
        return {
          label: 'Like New',
          badgeColor: 'bg-blue-100 text-blue-800 border-blue-200',
          description: 'Minimal to no signs of wear. Looks almost new.',
          detailedInfo: 'Excellent condition, barely worn, like new appearance',
        };
      case 'Good':
        return {
          label: 'Good Condition',
          badgeColor: 'bg-green-100 text-green-800 border-green-200',
          description: 'Used but remains in good condition.',
          detailedInfo: 'Normal wear for age, fully intact, ready to wear',
        };
      case 'Fair':
        return {
          label: 'Fair Condition',
          badgeColor: 'bg-orange-100 text-orange-800 border-orange-200',
          description: 'Noticeable signs of wear. Priced accordingly.',
          detailedInfo: 'Visible wear, character marks, discounted price',
        };
      case 'Brand New':
      default:
        return {
          label: 'Brand New',
          badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
          description: 'Never worn, in original condition. Perfect for you.',
          detailedInfo: 'Never worn, original condition, perfect',
        };
    }
  };

  const conditionInfo = getConditionInfo();
  const isPreLoved = product.condition === 'Pre-loved' || product.condition === 'Pre-loved (Used)';

  // Get the correct karat value
  const getKaratValue = () => {
    if (product.karat && product.karat.trim() !== '' && product.karat !== 'N/A') {
      return product.karat;
    }
    return 'N/A';
  };

  // Get the correct weight value
  const getWeightValue = () => {
    if (product.weight !== undefined && product.weight !== null) {
      if (typeof product.weight === 'number') {
        return product.weight.toFixed(2) + 'g';
      }
      if (
        typeof product.weight === 'string' &&
        product.weight.trim() !== '' &&
        product.weight !== 'N/A'
      ) {
        return product.weight;
      }
    }
    return 'N/A';
  };

  // Get the correct type/goldType value
  const getTypeValue = () => {
    if (product.goldType && product.goldType.trim() !== '' && product.goldType !== 'N/A') {
      return product.goldType;
    }

    const typeFromApi = extendedProduct.type;
    if (
      typeFromApi &&
      typeof typeFromApi === 'string' &&
      typeFromApi.trim() !== '' &&
      typeFromApi !== 'N/A'
    ) {
      return typeFromApi;
    }

    return 'Standard';
  };

  // Format karat to display properly
  const formatKarat = (karat) => {
    if (!karat || karat === 'N/A') return 'N/A';
    return karat;
  };

  // Format weight to display properly
  const formatWeight = (weight) => {
    if (!weight || weight === 'N/A') return 'N/A';
    return weight;
  };

  // Format gold type
  const formatGoldType = (type) => {
    if (!type || type === 'N/A') return 'Standard';
    return type;
  };

  const rawKarat = getKaratValue();
  const rawWeight = getWeightValue();
  const rawType = getTypeValue();

  const displayKarat = formatKarat(rawKarat);
  const displayWeight = formatWeight(rawWeight);
  const displayGoldType = formatGoldType(rawType);

  const totalPrice = displayPrice * detailQuantity;

  const currentImage = getCurrentImage();
  const hasImages = uniqueImages.length > 0;
  const hasMultipleImages = uniqueImages.length > 1;

  const isAnyProcessing = isAddToCartProcessing || isCheckoutProcessing;

  return React.createElement(
    'div',
    {
      className: 'fixed inset-0 z-[300] flex items-center justify-center',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 398 },
    },
    React.createElement('div', {
      className: 'absolute inset-0 bg-stone-900/80 backdrop-blur-md',
      onClick: onClose,
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 399 },
    }),

    React.createElement(
      'div',
      {
        className:
          'relative bg-white w-full max-w-5xl h-[90vh] md:h-auto md:max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl flex flex-col md:flex-row animate-fade-up-enter fade-up-active mx-4 z-[301]',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 404 },
      },
      React.createElement(
        'button',
        {
          onClick: onClose,
          className:
            'absolute top-4 right-4 z-20 p-2 bg-white/80 rounded-full hover:bg-stone-100 text-stone-500 hover:text-stone-900 transition-colors',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 405 },
        },

        React.createElement(X, {
          size: 20,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 409 },
        })
      ),

      /* Left: Gallery */
      React.createElement(
        'div',
        {
          className:
            'w-full md:w-1/2 bg-stone-50 p-8 flex flex-col items-center justify-start relative',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 413 },
        },
        hasImages
          ? React.createElement(
              React.Fragment,
              null,
              /* Main Image */
              React.createElement(
                'div',
                {
                  className: 'relative w-full aspect-square max-w-md mb-4 group',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 417 },
                },
                React.createElement('img', {
                  src: currentImage,
                  alt: product.name,
                  className: 'w-full h-full object-cover shadow-sm transition-opacity duration-300',
                  onError: handleMainImageError,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 418 },
                }),

                /* Badge on Image */
                showPromo
                  ? React.createElement(
                      'div',
                      {
                        className: 'absolute top-4 left-4 z-10',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 427 },
                      },
                      React.createElement(
                        'span',
                        {
                          className:
                            'bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 428 },
                        },
                        React.createElement(Clock, {
                          size: 16,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 429 },
                        }),
                        ' ',
                        promoLabel
                      )
                    )
                  : isPreOrder
                    ? React.createElement(
                        'div',
                        {
                          className: 'absolute top-4 left-4 z-10',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 433 },
                        },
                        React.createElement(
                          'span',
                          {
                            className:
                              'bg-purple-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 434 },
                          },
                          React.createElement(Clock, {
                            size: 16,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 435 },
                          }),
                          ' PRE-ORDER'
                        )
                      )
                    : React.createElement(
                        'div',
                        {
                          className: 'absolute top-4 left-4 z-10',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 439 },
                        },
                        React.createElement(
                          'span',
                          {
                            className: `px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider shadow-md ${conditionInfo.badgeColor}`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 440 },
                          },
                          conditionInfo.label
                        )
                      ),

                /* Navigation Arrows */
                hasMultipleImages &&
                  React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(
                      'button',
                      {
                        onClick: prevImage,
                        className: `absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all ${
                          selectedImage === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
                        }`,
                        disabled: selectedImage === 0,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 449 },
                      },

                      React.createElement(ChevronLeft, {
                        size: 20,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 456 },
                      })
                    ),
                    React.createElement(
                      'button',
                      {
                        onClick: nextImage,
                        className: `absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md transition-all ${
                          selectedImage === uniqueImages.length - 1
                            ? 'opacity-30 cursor-not-allowed'
                            : 'opacity-100'
                        }`,
                        disabled: selectedImage === uniqueImages.length - 1,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 458 },
                      },

                      React.createElement(ChevronRight, {
                        size: 20,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 465 },
                      })
                    )
                  )
              ),

              /* Thumbnail Gallery */
              uniqueImages.length > 0 &&
                React.createElement(
                  'div',
                  {
                    className: 'w-full max-w-md',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 473 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'flex items-center justify-between mb-3',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 474 },
                    },
                    React.createElement(
                      'h4',
                      {
                        className: 'text-xs uppercase tracking-widest text-stone-500 font-medium',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 475 },
                      },
                      hasMultipleImages ? 'MORE PHOTOS' : 'PRODUCT PHOTO'
                    ),
                    React.createElement(
                      'span',
                      {
                        className:
                          'text-[10px] text-stone-400 bg-stone-200/50 px-2 py-1 rounded-full',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 478 },
                      },
                      uniqueImages.length,
                      ' ',
                      uniqueImages.length === 1 ? 'image' : 'images'
                    )
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'flex gap-2 overflow-x-auto pb-2 scrollbar-hide',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 482 },
                    },
                    uniqueImages.map((img, index) =>
                      React.createElement(
                        'button',
                        {
                          key: index,
                          onClick: () => setSelectedImage(index),
                          className: `flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden transition-all ${
                            selectedImage === index
                              ? 'ring-2 ring-gold-600 opacity-100'
                              : 'ring-1 ring-stone-200 opacity-60 hover:opacity-100 hover:ring-gold-400'
                          }`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 484 },
                        },

                        React.createElement('img', {
                          src: img,
                          alt: `${product.name} view ${index + 1}`,
                          className: 'w-full h-full object-cover',
                          onError: () => handleImageError(index),
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 493 },
                        })
                      )
                    )
                  ),

                  hasMultipleImages &&
                    React.createElement(
                      'div',
                      {
                        className: 'text-center mt-3',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 504 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: 'text-xs text-stone-400',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 505 },
                        },
                        'Viewing ',
                        selectedImage + 1,
                        ' of ',
                        uniqueImages.length
                      )
                    )
                )
            )
          : React.createElement(
              'div',
              {
                className:
                  'w-full aspect-square max-w-md bg-stone-100 flex items-center justify-center mb-4 relative',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 514 },
              },
              React.createElement('img', {
                src: PLACEHOLDER_IMAGE,
                alt: 'No image available',
                className: 'w-full h-full object-cover',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 515 },
              }),
              showPromo
                ? React.createElement(
                    'div',
                    {
                      className: 'absolute top-4 left-4 z-10',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 517 },
                    },
                    React.createElement(
                      'span',
                      {
                        className:
                          'bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 518 },
                      },
                      React.createElement(Clock, {
                        size: 16,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 519 },
                      }),
                      ' ',
                      promoLabel
                    )
                  )
                : isPreOrder
                  ? React.createElement(
                      'div',
                      {
                        className: 'absolute top-4 left-4 z-10',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 523 },
                      },
                      React.createElement(
                        'span',
                        {
                          className:
                            'bg-purple-600 text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider shadow-lg flex items-center gap-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 524 },
                        },
                        React.createElement(Clock, {
                          size: 16,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 525 },
                        }),
                        ' PRE-ORDER'
                      )
                    )
                  : React.createElement(
                      'div',
                      {
                        className: 'absolute top-4 left-4 z-10',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 529 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: `px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider shadow-md ${conditionInfo.badgeColor}`,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 530 },
                        },
                        conditionInfo.label
                      )
                    )
            ),

        isCheckoutProcessing &&
          React.createElement(
            'button',
            {
              onClick: () => {
                setIsCheckoutProcessing(false);
                setIsAddToCartProcessing(false);
                setStockError(null);
              },
              className: 'mt-4 text-xs text-stone-400 hover:text-stone-600 underline',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 539 },
            },
            'Reset (stuck?)'
          )
      ),

      /* Right: Details */
      React.createElement(
        'div',
        {
          className: 'w-full md:w-1/2 p-8 md:p-12 flex flex-col',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 553 },
        },
        React.createElement(
          'div',
          {
            className: 'mb-auto',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 554 },
          },
          React.createElement(
            'div',
            {
              className:
                'text-xs text-stone-400 uppercase tracking-widest mb-2 flex items-center gap-2 flex-wrap',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 555 },
            },
            React.createElement(
              'span',
              {
                className: 'font-medium',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 556 },
              },
              _optionalChain([
                product,
                'access',
                (_) => _.category,
                'optionalAccess',
                (_2) => _2.toUpperCase,
                'call',
                (_3) => _3(),
              ]) || 'JEWELRY'
            ),
            React.createElement('span', {
              className: 'w-1 h-1 bg-stone-300 rounded-full',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 557 },
            }),
            showPromo
              ? React.createElement(
                  'span',
                  {
                    className:
                      'bg-red-100 text-red-800 px-2 py-0.5 rounded-sm flex items-center gap-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 559 },
                  },
                  React.createElement(Clock, {
                    size: 12,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 560 },
                  }),
                  ' ',
                  promoLabel
                )
              : isPreOrder
                ? React.createElement(
                    'span',
                    {
                      className:
                        'bg-purple-100 text-purple-800 px-2 py-0.5 rounded-sm flex items-center gap-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 563 },
                    },
                    React.createElement(Clock, {
                      size: 12,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 564 },
                    }),
                    ' PRE-ORDER'
                  )
                : React.createElement(
                    'span',
                    {
                      className: `px-2 py-0.5 rounded-sm ${conditionInfo.badgeColor}`,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 567 },
                    },
                    conditionInfo.label
                  )
          ),

          React.createElement(
            'h2',
            {
              className: 'font-serif text-3xl md:text-4xl text-stone-900 mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 573 },
            },
            product.name
          ),

          /* Stock Status */
          React.createElement(
            'div',
            {
              className: 'mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 576 },
            },
            isOutOfStock
              ? React.createElement(
                  'span',
                  {
                    className:
                      'inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 578 },
                  },
                  React.createElement(AlertCircle, {
                    size: 14,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 579 },
                  }),
                  'OUT OF STOCK'
                )
              : stock > 0 && stock <= 5
                ? React.createElement(
                    'span',
                    {
                      className:
                        'inline-flex items-center gap-1 text-xs font-bold text-orange-600 bg-orange-50 px-3 py-1 rounded-full',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 583 },
                    },
                    React.createElement(Clock, {
                      size: 14,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 584 },
                    }),
                    'Only ',
                    stock,
                    ' left in stock'
                  )
                : null
          ),

          /* Price Display */
          React.createElement(
            'div',
            {
              className: 'flex items-center justify-between mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 591 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 592 } },
              showPromo
                ? React.createElement(
                    'div',
                    {
                      className: 'flex items-center gap-3',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 594 },
                    },
                    React.createElement(
                      'span',
                      {
                        className: 'text-2xl font-medium text-red-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 595 },
                      },
                      '₱ ',
                      _optionalChain([
                        promoPrice,
                        'optionalAccess',
                        (_4) => _4.toLocaleString,
                        'call',
                        (_5) => _5(),
                      ]) || '0'
                    ),
                    React.createElement(
                      'span',
                      {
                        className: 'text-lg text-stone-400 line-through',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 598 },
                      },
                      '₱ ',
                      _optionalChain([
                        product,
                        'access',
                        (_6) => _6.price,
                        'optionalAccess',
                        (_7) => _7.toLocaleString,
                        'call',
                        (_8) => _8(),
                      ]) || '0'
                    )
                  )
                : React.createElement(
                    'span',
                    {
                      className: 'text-2xl font-medium text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 603 },
                    },
                    '₱ ',
                    _optionalChain([
                      product,
                      'access',
                      (_9) => _9.price,
                      'optionalAccess',
                      (_10) => _10.toLocaleString,
                      'call',
                      (_11) => _11(),
                    ]) || '0'
                  )
            ),
            product.isBestSeller &&
              React.createElement(
                'span',
                {
                  className:
                    'bg-gold-50 text-gold-700 text-[10px] uppercase font-bold px-3 py-1 border border-gold-200',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 609 },
                },
                'BEST SELLER'
              )
          ),

          /* Stock Error Message */
          stockError &&
            React.createElement(
              'div',
              {
                className:
                  'mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-red-600 text-xs flex items-center gap-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 617 },
              },
              React.createElement(AlertCircle, {
                size: 14,
                className: 'flex-shrink-0',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 618 },
              }),
              React.createElement(
                'span',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 619 } },
                stockError
              )
            ),

          /* PROMO HERO SECTION */
          showPromo &&
            React.createElement(
              'div',
              {
                className:
                  'mb-8 p-6 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-300 rounded-lg shadow-md',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 625 },
              },
              React.createElement(
                'div',
                {
                  className: 'flex items-start gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 626 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'bg-red-600 rounded-full p-3 flex-shrink-0',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 627 },
                  },
                  React.createElement(Clock, {
                    size: 28,
                    className: 'text-white',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 628 },
                  })
                ),
                React.createElement(
                  'div',
                  {
                    className: 'flex-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 630 },
                  },
                  React.createElement(
                    'h3',
                    {
                      className: 'text-xl font-bold text-red-800 mb-2 flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 631 },
                    },
                    promoLabel,
                    ' - SPECIAL OFFER',
                    promoPercentage &&
                      React.createElement(
                        'span',
                        {
                          className: 'bg-red-600 text-white text-sm px-2 py-1 rounded-full',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 634 },
                        },
                        promoPercentage,
                        '% OFF'
                      )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'space-y-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 640 },
                    },
                    savings > 0 &&
                      React.createElement(
                        'p',
                        {
                          className: 'text-sm text-red-700',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 642 },
                        },
                        'You save: ₱ ',
                        savings.toLocaleString(),
                        ' (',
                        savingsPercentage,
                        '%)'
                      ),

                    promoEndDate &&
                      React.createElement(
                        'div',
                        {
                          className:
                            'flex items-center gap-2 text-sm text-red-700 bg-white/50 p-2 rounded',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 648 },
                        },
                        React.createElement(Calendar, {
                          size: 16,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 649 },
                        }),
                        React.createElement(
                          'span',
                          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 650 } },
                          'Offer ends ',
                          formatDate(promoEndDate)
                        )
                      ),

                    React.createElement(
                      'p',
                      {
                        className: 'text-xs text-red-600 mt-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 654 },
                      },
                      "Limited time offer. Don't miss out."
                    )
                  )
                )
              )
            ),

          /* PRE-ORDER HERO SECTION */
          !showPromo &&
            isPreOrder &&
            React.createElement(
              'div',
              {
                className:
                  'mb-8 p-6 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg shadow-md',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 665 },
              },
              React.createElement(
                'div',
                {
                  className: 'flex items-start gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 666 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'bg-purple-600 rounded-full p-3 flex-shrink-0',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 667 },
                  },
                  React.createElement(Clock, {
                    size: 28,
                    className: 'text-white',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 668 },
                  })
                ),
                React.createElement(
                  'div',
                  {
                    className: 'flex-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 670 },
                  },
                  React.createElement(
                    'h3',
                    {
                      className: 'text-xl font-bold text-purple-800 mb-2 flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 671 },
                    },
                    'PRE-ORDER ITEM',

                    product.isBestSeller &&
                      React.createElement(
                        'span',
                        {
                          className: 'bg-gold-500 text-white text-xs px-2 py-1 rounded-full',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 674 },
                        },
                        'Bestseller'
                      )
                  ),
                  React.createElement(
                    'p',
                    {
                      className: 'text-purple-700 mb-3',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 677 },
                    },
                    'This item is available for pre-order. Your reservation secures this piece before its official release.'
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'bg-white/80 rounded-lg p-3 border border-purple-200',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 681 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'flex items-center gap-3 mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 682 },
                      },
                      React.createElement(Calendar, {
                        size: 18,
                        className: 'text-purple-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 683 },
                      }),
                      React.createElement(
                        'span',
                        {
                          className: 'font-medium text-purple-800',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 684 },
                        },
                        preOrderReleaseDate
                          ? `Expected Release: ${formatReleaseDate(preOrderReleaseDate)}`
                          : 'Release Date: Coming Soon'
                      )
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'flex items-start gap-3',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 688 },
                      },
                      React.createElement(Info, {
                        size: 18,
                        className: 'text-purple-600 flex-shrink-0 mt-0.5',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 689 },
                      }),
                      React.createElement(
                        'p',
                        {
                          className: 'text-sm text-purple-600',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 690 },
                        },
                        'A down payment may be required to reserve this item. You will be notified when it is ready to ship.'
                      )
                    )
                  ),

                  React.createElement(
                    'div',
                    {
                      className: 'mt-3 grid grid-cols-2 gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 696 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'text-xs bg-white/60 p-2 rounded flex items-center gap-1',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 697 },
                      },
                      React.createElement(ShieldCheck, {
                        size: 14,
                        className: 'text-purple-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 698 },
                      }),
                      React.createElement(
                        'span',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 699 } },
                        'Secure reservation'
                      )
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'text-xs bg-white/60 p-2 rounded flex items-center gap-1',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 701 },
                      },
                      React.createElement(Calendar, {
                        size: 14,
                        className: 'text-purple-600',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 702 },
                      }),
                      React.createElement(
                        'span',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 703 } },
                        'Priority fulfillment'
                      )
                    )
                  )
                )
              )
            ),

          /* Condition Details Section */
          !showPromo &&
            !isPreOrder &&
            (isPreLoved || conditionInfo.label !== 'Brand New') &&
            React.createElement(
              'div',
              {
                className: `mb-6 p-4 rounded-sm border ${conditionInfo.badgeColor.replace('text', 'border').replace('bg', 'bg-opacity-30')}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 713 },
              },
              React.createElement(
                'div',
                {
                  className: 'flex items-start gap-3',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 714 },
                },
                React.createElement(AlertCircle, {
                  size: 18,
                  className: 'flex-shrink-0 mt-0.5',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 715 },
                }),
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 716 } },
                  React.createElement(
                    'h4',
                    {
                      className: 'text-xs font-bold uppercase mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 717 },
                    },
                    conditionInfo.label.toUpperCase(),
                    ' ITEM'
                  ),
                  React.createElement(
                    'p',
                    {
                      className: 'text-sm mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 720 },
                    },
                    conditionInfo.description
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'flex items-center gap-2 text-xs',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 721 },
                    },
                    React.createElement(ShieldCheck, {
                      size: 14,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 722 },
                    }),
                    React.createElement(
                      'span',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 723 } },
                      conditionInfo.detailedInfo
                    )
                  )
                )
              )
            ),

          React.createElement(
            'div',
            {
              className: 'border-t border-b border-stone-100 py-6 mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 730 },
            },
            React.createElement(
              'p',
              {
                className: 'text-stone-600 font-light leading-relaxed mb-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 731 },
              },
              product.description ||
                'Crafted with precision and care, this piece exemplifies the Sparkling Gems commitment to quality.'
            ),

            /* Specs for jewelry category */
            (product.category === 'jewelry' || !product.category) &&
              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-3 gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 737 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'text-center p-3 bg-stone-50 rounded-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 738 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'block text-[10px] text-stone-400 uppercase tracking-wider mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 739 },
                    },
                    'KARAT'
                  ),
                  React.createElement(
                    'span',
                    {
                      className: 'font-serif text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 740 },
                    },
                    displayKarat
                  )
                ),
                React.createElement(
                  'div',
                  {
                    className: 'text-center p-3 bg-stone-50 rounded-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 742 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'block text-[10px] text-stone-400 uppercase tracking-wider mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 743 },
                    },
                    'WEIGHT'
                  ),
                  React.createElement(
                    'span',
                    {
                      className: 'font-serif text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 744 },
                    },
                    displayWeight
                  )
                ),
                React.createElement(
                  'div',
                  {
                    className: 'text-center p-3 bg-stone-50 rounded-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 746 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'block text-[10px] text-stone-400 uppercase tracking-wider mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 747 },
                    },
                    'TYPE'
                  ),
                  React.createElement(
                    'span',
                    {
                      className: 'font-serif text-stone-900',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 748 },
                    },
                    displayGoldType
                  )
                )
              )
          ),

          /* Controls */
          React.createElement(
            'div',
            {
              className: 'flex flex-col gap-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 766 },
            },
            React.createElement(
              'div',
              {
                className: 'flex items-center justify-between mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 767 },
              },
              React.createElement(
                'span',
                {
                  className: 'text-xs uppercase font-bold text-stone-500',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 768 },
                },
                'QUANTITY'
              ),
              React.createElement(
                'div',
                {
                  className: 'flex items-center border border-stone-200',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 769 },
                },
                React.createElement(
                  'button',
                  {
                    onClick: () => {
                      setDetailQuantity(Math.max(1, detailQuantity - 1));
                      setStockError(null);
                    },
                    className:
                      'p-3 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors',
                    disabled: isAnyProcessing || isOutOfStock,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 770 },
                  },

                  React.createElement(Minus, {
                    size: 14,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 778 },
                  })
                ),
                React.createElement(
                  'span',
                  {
                    className: 'w-12 text-center font-mono text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 780 },
                  },
                  detailQuantity
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => {
                      setDetailQuantity(detailQuantity + 1);
                      setStockError(null);
                    },
                    className:
                      'p-3 text-stone-500 hover:text-stone-900 hover:bg-stone-50 transition-colors',
                    disabled:
                      isAnyProcessing || isOutOfStock || (stock > 0 && detailQuantity >= stock),
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 781 },
                  },

                  React.createElement(Plus, {
                    size: 14,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 789 },
                  })
                )
              ),
              !isOutOfStock &&
                stock > 0 &&
                React.createElement(
                  'span',
                  {
                    className: 'text-xs text-stone-400 ml-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 793 },
                  },
                  'Max: ',
                  stock
                )
            ),

            React.createElement(
              'div',
              {
                className: 'flex gap-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 799 },
              },
              /* WISHLIST BUTTON */
              React.createElement(
                'button',
                {
                  onClick: () => onToggleWishlist(product),
                  disabled: isAnyProcessing,
                  className: `px-4 border transition-colors rounded-sm ${
                    isInWishlist(product.id)
                      ? 'border-red-200 bg-red-50 text-red-500'
                      : 'border-stone-200 text-stone-400 hover:border-stone-400 hover:text-stone-900'
                  }`,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 801 },
                },

                React.createElement(Heart, {
                  size: 20,
                  fill: isInWishlist(product.id) ? 'currentColor' : 'none',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 810 },
                })
              )
            ),

            /* PROCEED TO CHECKOUT BUTTON */
            React.createElement(
              'button',
              {
                onClick: handleCheckout,
                disabled: isAnyProcessing || isOutOfStock,
                className: `w-full py-4 uppercase text-xs tracking-[0.2em] transition-colors shadow-xl flex items-center justify-center gap-2 rounded-sm font-bold ${
                  isOutOfStock
                    ? 'bg-stone-300 cursor-not-allowed text-stone-600'
                    : showPromo
                      ? 'bg-red-700 hover:bg-red-800 text-white'
                      : isPreOrder
                        ? 'bg-purple-700 hover:bg-purple-800 text-white'
                        : 'bg-gold-600 hover:bg-gold-700 text-stone-900'
                }`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 815 },
              },

              isOutOfStock
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(AlertCircle, {
                      size: 16,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 830 },
                    }),
                    'UNAVAILABLE'
                  )
                : React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(CreditCard, {
                      size: 16,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 835 },
                    }),
                    showPromo ? 'BUY NOW' : isPreOrder ? 'RESERVE NOW' : 'PROCEED TO CHECKOUT',
                    ' — ₱ ',
                    totalPrice.toLocaleString()
                  )
            ),

            /* Additional messaging */
            isPreOrder &&
              !showPromo &&
              React.createElement(
                'p',
                {
                  className: 'text-xs text-center text-purple-600 mt-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 843 },
                },
                'By placing a pre-order, you agree to our pre-order terms. No payment will be processed until the item ships.'
              ),

            showPromo &&
              React.createElement(
                'p',
                {
                  className: 'text-xs text-center text-red-600 mt-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 849 },
                },
                'Limited time offer. Price as marked.'
              )
          )
        ),

        React.createElement(
          'div',
          {
            className: 'mt-8 pt-6 border-t border-stone-100 grid grid-cols-2 gap-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 856 },
          },
          React.createElement(
            'div',
            {
              className: 'flex items-start gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 857 },
            },
            React.createElement(Truck, {
              size: 18,
              className: 'text-gold-600 mt-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 858 },
            }),
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 859 } },
              React.createElement(
                'h4',
                {
                  className: 'text-xs font-bold uppercase text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 860 },
                },
                'SHIPPING'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-[10px] text-stone-500 leading-tight mt-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 861 },
                },
                'Insured delivery within Philippines.'
              ),
              isPreOrder &&
                !showPromo &&
                React.createElement(
                  'p',
                  {
                    className: 'text-[9px] text-purple-600 mt-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 863 },
                  },
                  'Ships after release date'
                )
            )
          ),
          React.createElement(
            'div',
            {
              className: 'flex items-start gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 867 },
            },
            React.createElement(ShieldCheck, {
              size: 18,
              className: 'text-gold-600 mt-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 868 },
            }),
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 869 } },
              React.createElement(
                'h4',
                {
                  className: 'text-xs font-bold uppercase text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 870 },
                },
                'AUTHENTICITY'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-[10px] text-stone-500 leading-tight mt-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 871 },
                },
                'Certified genuine and verified.'
              ),
              isPreLoved &&
                !showPromo &&
                React.createElement(
                  'p',
                  {
                    className: 'text-[9px] text-amber-600 mt-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 873 },
                  },
                  'Pre-owned, fully authenticated'
                )
            )
          )
        )
      )
    )
  );
};

export default ProductDetailModal;
